import type { PlausibilityPrior } from "@sitescene/types";

import { type Column } from "@/components/data-table";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";

import { formatLogOdds, formatWhen, logOddsToPercent } from "./format";
import {
	draftLogOdds,
	isValueDraftValid,
	type PriorValueDraft,
} from "./prior-draft";

export interface PriorColumnsOptions {
	busy: boolean;
	edit: null | PriorRowEdit;
	onDelete: (id: string) => void;
	onEdit: (row: PlausibilityPrior) => void;
}

/** The row currently being edited in place, and its unsaved value. */
export interface PriorRowEdit {
	draft: PriorValueDraft;
	id: string;
	onCancel: () => void;
	onChange: (changes: Partial<PriorValueDraft>) => void;
	onSave: () => void;
}

/**
 * Columns shared by the global and scoped prior tables. Class, season, and
 * hours identify a row and never change; the row named by `edit` renders its
 * probability and notes as inputs, every other row renders plain text.
 */
export const priorColumns = ({
	busy,
	edit,
	onDelete,
	onEdit,
}: PriorColumnsOptions): Column<PlausibilityPrior>[] => {
	const editOf = (row: PlausibilityPrior) =>
		edit && edit.id === row.id ? edit : null;

	return [
		{ header: "Class", key: "class", render: (row) => row.classId },
		{ header: "Season", key: "season", render: (row) => row.season ?? "any" },
		{ header: "Hours", key: "hours", render: (row) => row.hourBucket ?? "any" },
		{
			header: "Log odds",
			key: "odds",
			render: (row) => {
				const e = editOf(row);
				if (!e) {
					return `${formatLogOdds(row.logPriorOdds)} (${logOddsToPercent(row.logPriorOdds)})`;
				}
				const logOdds = draftLogOdds(e.draft);
				return (
					<span className="flex items-center gap-1">
						<Input
							aria-label="Prior probability (%)"
							autoFocus
							inputClassName="w-20"
							inputMode="decimal"
							onChange={(percent) => e.onChange({ percent })}
							onKeyDown={(event) => {
								if (event.key === "Enter" && isValueDraftValid(e.draft)) {
									e.onSave();
								}
								if (event.key === "Escape") e.onCancel();
							}}
							value={e.draft.percent}
						/>
						<span className="text-xs text-tertiary">
							% = {Number.isNaN(logOdds) ? "—" : logOdds.toFixed(2)}
						</span>
					</span>
				);
			},
		},
		{
			header: "Source",
			key: "source",
			render: (row) =>
				`${row.source}${row.userEdited ? " · edited" : ""}${row.observationCount ? ` · n=${row.observationCount}` : ""}`,
		},
		{
			header: "Notes",
			key: "notes",
			render: (row) => {
				const e = editOf(row);
				if (!e) {
					return (
						<span className="text-xs text-secondary">{row.notes ?? ""}</span>
					);
				}
				return (
					<Input
						aria-label="Notes"
						onChange={(notes) => e.onChange({ notes })}
						onKeyDown={(event) => {
							if (event.key === "Enter" && isValueDraftValid(e.draft)) {
								e.onSave();
							}
							if (event.key === "Escape") e.onCancel();
						}}
						value={e.draft.notes}
					/>
				);
			},
		},
		{
			header: "Updated",
			key: "updated",
			render: (row) => formatWhen(row.updatedAt),
		},
		{
			header: "",
			key: "actions",
			render: (row) => {
				const e = editOf(row);
				if (e) {
					return (
						<span className="flex gap-2">
							<Button
								isDisabled={busy || !isValueDraftValid(e.draft)}
								onClick={e.onSave}
								size="sm"
								type="button"
							>
								Save
							</Button>
							<Button
								color="secondary"
								isDisabled={busy}
								onClick={e.onCancel}
								size="sm"
								type="button"
							>
								Cancel
							</Button>
						</span>
					);
				}
				return (
					<span className="flex gap-2">
						<Button
							color="secondary"
							isDisabled={busy || edit !== null}
							onClick={() => onEdit(row)}
							size="sm"
							type="button"
						>
							Edit
						</Button>
						<Button
							color="primary-destructive"
							isDisabled={busy}
							onClick={() => onDelete(row.id)}
							size="sm"
							type="button"
						>
							Delete
						</Button>
					</span>
				);
			},
		},
	];
};
